import { isEmpty } from 'lodash';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { Image } from '@/components/image';
import { EmissionsReport } from '@/components/modules/emissions/report';
import { EmissionsReportSkeleton } from '@/components/modules/emissions/report/skeleton';
import { Text } from '@/components/text';
import { dialogStyles } from '@/components/ui/dialog';
import { UserMessage } from '@/components/ui/user-message';
import { CONTACT_FORM_FIELDS, ENQUIRY_TYPES } from '@/lib/constants';
import { ShipmentCargo } from '@/lib/models/shipment/types';
import { EmissionsData } from '@/types/api-response-types';
import { tv } from '@/utils/styles';

interface ShipmentEmissionsStatementProps {
  cargo?: ShipmentCargo | ShipmentCargo[];
  updatedAt?: string;
  isLoading?: boolean;
  canSeeEmissions?: boolean;
  emissionsData?: EmissionsData;
}

const contactQueryParams = (cargo?: ShipmentCargo) => ({
  [CONTACT_FORM_FIELDS.SUBJECT_OF_ENQUIRY]: 'Emissions report request',
  [CONTACT_FORM_FIELDS.ENQUIRY_TYPE]: ENQUIRY_TYPES.API_ENQUIRY,
  [CONTACT_FORM_FIELDS.MESSAGE]: `Hi, I'm interested in an emissions report for my shipment. The cargo details are: ${cargo ? `${cargo.type ? cargo.type : 'unknown'}, ${cargo.weight ? `${cargo.weight}kg` : 'unknown'}, ${cargo.volume ? `${cargo.volume}cbm` : 'unknown'}` : 'unknown'}. Thanks!`,
});

const ShipmentEmissionsStatement = ({
  cargo,
  updatedAt,
  emissionsData,
  canSeeEmissions = false,
  isLoading = false,
}: ShipmentEmissionsStatementProps) => {
  const { base, report } = styles({ canSeeEmissions });
  const { description, logo } = dialogStyles();

  return (
    <div className={base()}>
      {isLoading && <EmissionsReportSkeleton />}
      {!isLoading && emissionsData && !isEmpty(emissionsData?.emissions) && (
        <div className={report()}>
          <EmissionsReport
            emissions={emissionsData.emissions}
            vehicles={emissionsData.vehicles}
            locations={emissionsData.locations}
            cargo={cargo}
            dateGenerated={updatedAt}
            isRealData={canSeeEmissions}
          />
        </div>
      )}
      {!canSeeEmissions && (
        <div className="absolute inset-0 mx-auto my-12 h-fit w-fit rounded-md border border-border-grey bg-white p-6 shadow-md">
          <div className={logo()}>
            <Image src="/assets/icons/logo-colored.svg" alt="Fluent Cargo Logo" />
          </div>
          <Heading variant="h3" className="!m-0">
            Get in touch for shipment emission reports
          </Heading>
          <Text className={description()}>Please contact us if you are interested in shipment emission reports.</Text>
          <div className="space-x-4">
            <Button
              variant="primaryLight"
              className="mt-4"
              href={`/contact?${new URLSearchParams(contactQueryParams(Array.isArray(cargo) ? cargo[0] : cargo)).toString()}`}
            >
              Contact us
            </Button>
          </div>
        </div>
      )}

      {!isLoading && isEmpty(emissionsData?.emissions) && (
        <div className="italic">
          <UserMessage
            heading="Error loading emissions report"
            body="Sorry, the emissions report for this shipment could not be loaded. Please try refreshing the page or try again later. If this issue continues to persist, please let us know."
          />
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative mx-4',
    report: '',
  },
  variants: {
    canSeeEmissions: {
      false: {
        report: 'pointer-events-none blur',
      },
    },
  },
});

export { ShipmentEmissionsStatement };
